import Immutable from 'immutable';

/**
 * Throw an error if there is no state key provided
 * @param {Object} action The action object
 * @private
 */
const _checkStateKey = (action) => {
    if (!action.stateKey) {
        throw new Error(`Action sent to form reducer has no stateKey: ${JSON.stringify(action)}`);
    }
};

/**
 * Car Specific form reducers
 * @type {Object}
 */
export const formReducerCases = {
    /**
     * Sets Rego Lookup Payload
     * @param {Immutable.Map} state The app state
     * @param {Object} action The action
     * @returns {Immutable.Map} The updated form state
     */
    SET_VEHICLE_FORM_DATA: (state, action) => {
        _checkStateKey(action);
        const {
            make, model, fuel, body, transmission, colour, year, redbookCode, redbookCodeLong,
            nviCode, glassCode, marketValue, alarmFitted, immobiliserFitted, vehicleDescription, badge
        } = action.payload;
        const vehicleState = state.getIn(['coverDetail', 'vehicle']);
        // this has been added to fix the issue when its the new year and the user thinks they have the latest model car
        // e.g 2019 but the actual model is 2018
        // redbook knows that the model is 2018 so it returns 2018
        // to prevent us confusing the user we need to display 2019
        const newYearFix = vehicleState.get('year') || year;
        const vehicleData = vehicleState.merge({
            badge: badge.code,
            body: body.code,
            fuel: fuel.code,
            make: make.code,
            makeLabel: make.label,
            model: model.code,
            transmission: transmission.code,
            // Initialise colour to null. rego-lookup-api returns colour whereas oldMotorWebRegoLookup does not.
            // Manually setting colour to null as business is not ready for pre-selected colour at this step yet.
            colour: null,
            year: newYearFix,
            redbookCode,
            nviCode,
            glassCode,
            marketValue,
            alarmFitted,
            immobiliserFitted,
            description: vehicleDescription,
            redbookCodeLong
        });
        return state.setIn(['coverDetail', 'vehicle'], vehicleData);
    },
    SET_VEHICLE_INDIVIDUAL_FORM_DATA: (state, action) => {
        _checkStateKey(action);
        // Do not auto progress journey and update the model if user is on anonymous prefill
        if (state.getIn(['helpers', 'prefill', 'lastViewedPage']) === '/car-insurance/journey/welcome_back') {
            return state;
        }
        return state.setIn(['coverDetail', 'vehicle', action.identifier], Immutable.fromJS(action.value));
    },
    SET_OTHER_ACCESSORIES_STANDARD: (state, action) => {
        _checkStateKey(action);
        const hasOtherAccessories = state.getIn(['coverDetail', 'vehicle', 'hasOtherAccessories']);
        if (hasOtherAccessories === 'UNSURE') {
            const factoryOptions = state.getIn(['coverDetail', 'vehicle', 'factoryOptions']);
            if (factoryOptions && factoryOptions.size > 0) {
                return state.setIn(['coverDetail', 'vehicle', 'hasOtherAccessories'], 'YES');
            }
        }
        return state;
    },
    SET_OTHER_ACCESSORIES_NON_STANDARD: (state, action) => {
        _checkStateKey(action);
        let hasOtherAccessories = state.getIn(['coverDetail', 'vehicle', 'hasOtherAccessories']);

        if (!hasOtherAccessories) {
            hasOtherAccessories = 'UNSURE';
        }

        if (hasOtherAccessories === 'YES' || hasOtherAccessories === 'UNSURE') {
            const nonStandardAccessories = state.getIn(['coverDetail', 'vehicle', 'nonStandardAccessories']);
            if (hasOtherAccessories === 'UNSURE') {
                if (nonStandardAccessories && nonStandardAccessories.size > 0) {
                    return state.setIn(['coverDetail', 'vehicle', 'hasOtherAccessories'], 'YES');
                }
                return state.setIn(['coverDetail', 'vehicle', 'hasOtherAccessories'], 'NO');
            }
            const factoryOptions = state.getIn(['coverDetail', 'vehicle', 'factoryOptions']);
            if ((!factoryOptions || factoryOptions.size === 0) && (!nonStandardAccessories || nonStandardAccessories.size === 0)) {
                return state.setIn(['coverDetail', 'vehicle', 'hasOtherAccessories'], 'NO');
            }
        } else {
            let newState = state;
            const factoryOptions = state.getIn(['coverDetail', 'vehicle', 'factoryOptions']);
            if (factoryOptions && factoryOptions.size > 0) {
                newState = newState.removeIn(['coverDetail', 'vehicle', 'factoryOptions']);
            }
            const nonStandardAccessories = state.getIn(['coverDetail', 'vehicle', 'nonStandardAccessories']);
            if (nonStandardAccessories && nonStandardAccessories.size > 0) {
                newState = newState.removeIn(['coverDetail', 'vehicle', 'nonStandardAccessories']);
            }
            return newState;
        }
        return state;
    },
    SET_CLAIM_DETAILS_FORM_VALUE_WITH_RESET: (state, action) => {
        _checkStateKey(action);
        return state.setIn(action.identifier.split('.'), Immutable.List(action.value));
    }
};
